// src/events/manage_events_dashboard.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FiSearch,
  FiPlay,
  FiEdit3,
  FiClock,
  FiEyeOff,
  FiChevronRight,
  FiTag,
  FiPlus,
  FiUser,
  FiHash,
  FiX,
  FiTrash2,
  FiMapPin,
  FiCalendar,
  FiUsers,
} from "react-icons/fi";
import { FaCalendarAlt, FaQrcode } from "react-icons/fa";
import Logo from "../assets/events-logo.png";

import { db } from "../firebase";
import {
  addDoc,
  collection,
  serverTimestamp,
  getDocs,
  query as fsQuery,
  where,
  limit,
  deleteDoc,
  doc,
  onSnapshot,
} from "firebase/firestore";

import { getStorage, ref as sRef, getDownloadURL, deleteObject } from "firebase/storage";

import OrganiserProfile from "./organiser_profile";

/* ──────────────────────────────────────────────────────────
   THEME
   ────────────────────────────────────────────────────────── */
const COLORS = {
  primary: "#f20c53",
  secondary: "#fb6c33",
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  page: "#f5f6f8",
  card: "#ffffff",
};

const MENU = [
  { key: "LIVE", label: "Live", icon: <FiPlay /> },
  { key: "REVIEW", label: "Review", icon: <FiClock /> },
  { key: "DRAFT", label: "Draft", icon: <FiEdit3 /> },
  { key: "PAST", label: "Past", icon: <FiClock /> },
  { key: "UNLISTED", label: "Unlisted & Closed", icon: <FiEyeOff /> },
];

const TABS = [
  { key: "MANAGE", label: "Manage Events", icon: <FaCalendarAlt /> },
  { key: "OFFER", label: "Create Offer", icon: <FiTag /> },
  { key: "SCAN", label: "Scanning Guest List", icon: <FaQrcode /> },
];

/* ──────────────────────────────────────────────────────────
   SESSION + ORGANISER HELPERS
   ────────────────────────────────────────────────────────── */
const COLL_SIGNUPS = "organiser_signup";

async function findSignupByMobile(mobile) {
  const col = collection(db, COLL_SIGNUPS);
  let snap = await getDocs(
    fsQuery(col, where("general.contact.mobile", "==", mobile), limit(1))
  );
  if (!snap.empty) {
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  }
  snap = await getDocs(fsQuery(col, where("mobileVerified", "==", mobile), limit(1)));
  if (!snap.empty) {
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  }
  return null;
}

function pickContactName(signup) {
  if (!signup) return "—";
  const g = signup.general || {};
  const c = g.contact || {};
  return c.name || c.fullName || signup.contactName || signup.name || "—";
}

function pickOrganiserUID(signup) {
  if (!signup) return "—";
  return signup.organiser_UID || signup.organiserUID || signup.uid || signup.orgUID || "—";
}

function getSessionMobile() {
  try {
    const raw = localStorage.getItem("events_session");
    const j = raw ? JSON.parse(raw) : null;
    const m = j?.mobile || j?.phone || "";
    return String(m).replace(/\D/g, "").slice(-10);
  } catch {
    return "";
  }
}

/* ──────────────────────────────────────────────────────────
   DRAFT EVENTS FETCHER
   ────────────────────────────────────────────────────────── */
const STATUS_FILTERS = {
  LIVE: ["live"],
  REVIEW: ["review", "pending_review", "submitted"],
  DRAFT: ["draft"],
  PAST: ["past", "completed", "ended", "live"],
  UNLISTED: ["unlisted", "closed", "close"],
};

async function fetchEventsForMobile(mobile, menuKey) {
  const coll = collection(db, "create_new_event");
  const statuses = STATUS_FILTERS[menuKey] || [];
  const statusSet = new Set(statuses);
  const normalizeStatus = (value) => String(value || "").trim().toLowerCase();
  const belongsToMobile = (ev) =>
    !mobile ||
    String(ev.createdByMobile || ev.organiser?.mobile || ev.organizerMobile || "")
      .replace(/\D/g, "")
      .endsWith(mobile);

  if (!statuses.length) return [];

  // Prefer compound query (status + createdByMobile)
  try {
    const statusFilter =
      statuses.length === 1 ? where("status", "==", statuses[0]) : where("status", "in", statuses);
    const q1 = mobile ? fsQuery(coll, statusFilter, where("createdByMobile", "==", mobile)) : fsQuery(coll, statusFilter);
    const snap = await getDocs(q1);
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((ev) => statusSet.has(normalizeStatus(ev.status)) && belongsToMobile(ev))
      .filter((ev) => eventBelongsInMenu(ev, menuKey));
  } catch {
    // Fallback: single filter + client-side filter
    const statusFilter =
      statuses.length === 1 ? where("status", "==", statuses[0]) : where("status", "in", statuses);
    const snap = await getDocs(fsQuery(coll, statusFilter));
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((ev) => statusSet.has(normalizeStatus(ev.status)) && belongsToMobile(ev))
      .filter((ev) => eventBelongsInMenu(ev, menuKey));
  }
}

/* ──────────────────────────────────────────────────────────
   MAIN COMPONENT
   ────────────────────────────────────────────────────────── */
export default function ManageEventsDashboard() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("MANAGE");

  const [search, setSearch] = useState(""); // name/ID search
  const [activeMenu, setActiveMenu] = useState(() => menuFromTabParam(searchParams.get("tab")));
  const [hoveredMenu, setHoveredMenu] = useState("");

  // Offer tab state
  const [offerTitle, setOfferTitle] = useState("");
  const [scope, setScope] = useState("event");
  const [offerType, setOfferType] = useState("percent");
  const [discountValue, setDiscountValue] = useState("");
  const [maxDiscount, setMaxDiscount] = useState("");
  const [minOrderAmount, setMinOrderAmount] = useState("");
  const [code, setCode] = useState("");
  const [usageLimit, setUsageLimit] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [appliesTo, setAppliesTo] = useState("");
  const [terms, setTerms] = useState("");
  const [status, setStatus] = useState("active");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [saveError, setSaveError] = useState("");

  // Header organiser
  const [contactName, setContactName] = useState("—");
  const [organiserUID, setOrganiserUID] = useState("—");
  const [loadingHeader, setLoadingHeader] = useState(true);

  // Profile drawer
  const [profileOpen, setProfileOpen] = useState(false);

  // Events by sidebar status
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [eventError, setEventError] = useState("");
  const [deletingIds, setDeletingIds] = useState({}); // { [id]: true }
  const [menuCounts, setMenuCounts] = useState({});

  // Load organiser header
  useEffect(() => {
    let mounted = true;
    (async () => {
      const mobile = getSessionMobile();
      if (!mobile) {
        setLoadingHeader(false);
        return;
      }
      try {
        const signup = await findSignupByMobile(mobile);
        if (!mounted) return;
        setContactName(pickContactName(signup));
        setOrganiserUID(pickOrganiserUID(signup));
      } catch (e) {
        console.warn("Signup fetch failed:", e);
      } finally {
        if (mounted) setLoadingHeader(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // ESC to close profile
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setProfileOpen(false);
    }
    if (profileOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [profileOpen]);

  useEffect(() => {
    setActiveMenu(menuFromTabParam(searchParams.get("tab")));
  }, [searchParams]);

  useEffect(() => {
    const mobile = getSessionMobile();
    const coll = collection(db, "create_new_event");
    const belongsToMobile = (ev) =>
      !mobile ||
      String(ev.createdByMobile || ev.organiser?.mobile || ev.organizerMobile || "")
        .replace(/\D/g, "")
        .endsWith(mobile);
    const applySnapshot = (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter(belongsToMobile);
      setMenuCounts(buildMenuCounts(rows));
    };
    const handleError = (err) => {
      console.warn("Live event counts failed:", err);
      setMenuCounts({});
    };
    try {
      const q = mobile ? fsQuery(coll, where("createdByMobile", "==", mobile)) : coll;
      return onSnapshot(q, applySnapshot, handleError);
    } catch {
      return onSnapshot(coll, applySnapshot, handleError);
    }
  }, []);

  // Fetch events when switching sidebar status
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingEvents(true);
      setEventError("");
      try {
        const mobile = getSessionMobile();
        const rows = await fetchEventsForMobile(mobile, activeMenu);
        if (!mounted) return;
        setEvents(rows);
      } catch (e) {
        console.error(e);
        if (!mounted) return;
        setEventError(`Could not load ${labelFor(activeMenu).toLowerCase()} events.`);
        setEvents([]);
      } finally {
        mounted && setLoadingEvents(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [activeMenu]);

  // Filter events by search text (name, id, venue, category, status)
  const filteredEvents = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return events;
    return events.filter((d) => {
      const haystack = [
        d.id,
        d.name,
        d.category,
        d.subCategory,
        d.status,
        d?.schedule?.venueName,
        d?.venueDetails?.venueName,
        d?.schedule?.venueDetails?.venueName,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [events, search]);

  // Offer save
  const onSaveOffer = async (e) => {
    e.preventDefault();
    setSaveError("");
    setSaveMsg("");

    if (!offerTitle.trim()) return setSaveError("Enter offer title.");
    if (!code.trim()) return setSaveError("Enter promo code.");
    if ((offerType === "percent" || offerType === "flat") && !discountValue) {
      return setSaveError("Enter discount value.");
    }
    if (!appliesTo.trim()) {
      return setSaveError(
        scope === "event"
          ? "Enter Event ID(s) this offer applies to."
          : "Enter Venue ID(s) this offer applies to."
      );
    }

    const payload = {
      title: offerTitle.trim(),
      scope,
      type: offerType,
      code: code.trim().toUpperCase(),
      discount: {
        percent: offerType === "percent" ? Number(discountValue) || null : null,
        flat: offerType === "flat" ? Number(discountValue) || null : null,
        bogo: offerType === "bogo",
      },
      maxDiscount: maxDiscount ? Number(maxDiscount) : null,
      minOrderAmount: minOrderAmount ? Number(minOrderAmount) : null,
      usageLimit: usageLimit ? Number(usageLimit) : null,
      validFrom: validFrom || null,
      validTo: validTo || null,
      appliesTo:
        scope === "event"
          ? { eventIds: splitIds(appliesTo), venueIds: [] }
          : { eventIds: [], venueIds: splitIds(appliesTo) },
      terms: terms.trim() || null,
      status,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    setSaving(true);
    try {
      await addDoc(collection(db, "event_venue_offers"), payload);
      setSaving(false);
      setSaveMsg("Offer created successfully.");
      setOfferTitle("");
      setScope("event");
      setOfferType("percent");
      setDiscountValue("");
      setMaxDiscount("");
      setMinOrderAmount("");
      setCode("");
      setUsageLimit("");
      setValidFrom("");
      setValidTo("");
      setAppliesTo("");
      setTerms("");
      setStatus("active");
    } catch (err) {
      console.error(err);
      setSaving(false);
      setSaveError("Could not save offer. Please try again.");
    }
  };

  // Create new event
  const onNewEventClick = () => {
    nav("/events/create_event_form1");
  };

  // Delete a draft event (Firestore + referenced Storage files best-effort)
  const onDeleteDraft = async (id) => {
    const ev = events.find((d) => d.id === id);
    const ok = window.confirm("Delete this draft event permanently?");
    if (!ok) return;

    setDeletingIds((m) => ({ ...m, [id]: true }));
    try {
      // Delete Firestore document
      await deleteDoc(doc(db, "create_new_event", id));

      // Best-effort delete referenced storage files (if any)
      try {
        const storage = getStorage();
        const paths = [
          ev?.images?.horizontalBanner?.path,
          ev?.images?.verticalPoster?.path,
          ev?.images?.headerImagePath,
          ev?.images?.verticalPosterPath,
        ].filter(Boolean);
        for (const p of paths) {
          try {
            await deleteObject(sRef(storage, p));
          } catch {
            // ignore individual failures (could be missing or permission)
          }
        }
      } catch {
        // ignore storage cleanup errors
      }

      // Remove locally
      setEvents((prev) => prev.filter((d) => d.id !== id));
    } catch (e) {
      console.error(e);
      alert("Could not delete this draft. Please try again.");
    } finally {
      setDeletingIds((m) => {
        const copy = { ...m };
        delete copy[id];
        return copy;
      });
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: COLORS.page,
        display: "grid",
        gridTemplateRows: "72px 1fr",
      }}
    >
      <style>{`@keyframes countPop{0%{transform:scale(.82);opacity:.65}70%{transform:scale(1.08);opacity:1}100%{transform:scale(1);opacity:1}}`}</style>
      {/* Header */}
      <header
        style={{
          background: "#1f2937",
          color: "#fff",
          display: "grid",
          gridTemplateColumns: "220px 1fr auto 56px",
          alignItems: "center",
          gap: 16,
          padding: "0 16px",
          borderBottom: `1px solid rgba(255,255,255,0.08)`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src={Logo} alt="Events" style={{ height: 36 }} />
        </div>

        <div
          style={{
            textAlign: "center",
            fontWeight: 900,
            fontSize: 28,
            letterSpacing: 0.2,
          }}
        >
          Manage Events
        </div>

        <div style={{ display: "grid", gap: 4, justifyItems: "end" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
            <FiUser />
            <span>{loadingHeader ? "Loading…" : `Hi, ${contactName}`}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
            <FiHash />
            <span>{loadingHeader ? "—" : organiserUID}</span>
          </div>
        </div>

        <div
          title="Open profile"
          onClick={() => setProfileOpen(true)}
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
            display: "grid",
            placeItems: "center",
            border: "1px solid rgba(255,255,255,0.25)",
            cursor: "pointer",
            userSelect: "none",
          }}
        >
          <FiUser />
        </div>
      </header>

      {/* Main */}
      <main
        style={{
          minHeight: "calc(100vh - 72px)",
          background: COLORS.page,
          padding: "18px 18px 24px",
        }}
      >
        {/* Tabs */}
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 10,
              margin: "6px auto 14px",
            }}
          >
            {TABS.map((t) => {
              const active = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveTab(t.key)}
                  style={{
                    borderRadius: 999,
                    padding: "10px 16px",
                    border: `1px solid ${active ? "transparent" : COLORS.border}`,
                    background: active
                      ? `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`
                      : "#fff",
                    color: active ? "#fff" : COLORS.text,
                    fontWeight: 800,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    boxShadow: active
                      ? "0 12px 26px rgba(242,12,83,.20)"
                      : "0 1px 2px rgba(0,0,0,.05)",
                    transition: "transform .15s ease, box-shadow .15s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.transform = "translateY(-1px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <span style={{ display: "grid", placeItems: "center" }}>{t.icon}</span>
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Search (Manage tab only) */}
        {activeTab === "MANAGE" && (
          <form onSubmit={(e) => e.preventDefault()} style={{ maxWidth: 1100, margin: "0 auto" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  position: "relative",
                  background: COLORS.card,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 12,
                  height: 48,
                  display: "grid",
                  alignItems: "center",
                  boxShadow: "0 1px 2px rgba(0,0,0,.05)",
                }}
              >
                <FiSearch
                  size={18}
                  style={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: COLORS.subtext,
                  }}
                />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search events by name or ID"
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    outline: "none",
                    padding: "0 12px 0 40px",
                    background: "transparent",
                    fontSize: 14,
                    color: COLORS.text,
                  }}
                />
              </div>

              <button
                type="button"
                style={{
                  height: 48,
                  border: "none",
                  borderRadius: 12,
                  padding: "0 16px",
                  fontWeight: 800,
                  color: "#fff",
                  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
                title="Search"
              >
                <FiSearch />
                Search
              </button>
            </div>
          </form>
        )}

        <div style={{ height: activeTab === "MANAGE" ? 16 : 12 }} />

        {/* Body */}
        {activeTab === "MANAGE" && (
          <div
            style={{
              maxWidth: 1100,
              margin: "0 auto",
              display: "grid",
              gridTemplateColumns: "280px 1fr",
              gap: 16,
              alignItems: "start",
            }}
          >
            {/* Sidebar */}
            <aside>
              <div style={{ display: "grid", gap: 12 }}>
                {MENU.map((m) => {
                  const isActive = activeMenu === m.key;
                  const isHover = hoveredMenu === m.key;
                  const count = menuCounts[m.key] ?? 0;
                  return (
                    <button
                      key={m.key}
                      type="button"
                      onMouseEnter={() => setHoveredMenu(m.key)}
                      onMouseLeave={() => setHoveredMenu("")}
                      onClick={() => setActiveMenu(m.key)}
                      aria-pressed={isActive}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        borderRadius: 14,
                        border: `1px solid ${isActive ? "transparent" : COLORS.border}`,
                        background: isActive
                          ? `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`
                          : "#fff",
                        color: isActive ? "#fff" : COLORS.text,
                        padding: 16,
                        cursor: "pointer",
                        display: "grid",
                        gridTemplateColumns: "28px 1fr auto",
                        gap: 10,
                        alignItems: "center",
                        boxShadow: isActive
                          ? "0 14px 28px rgba(242,12,83,.18)"
                          : "0 1px 2px rgba(0,0,0,.05)",
                        transform: isHover && !isActive ? "translateX(4px)" : "translateX(0)",
                        transition:
                          "box-shadow .18s ease, transform .18s ease, background .18s ease, color .18s ease, border-color .18s ease",
                      }}
                    >
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          background: isActive ? "rgba(255,255,255,.15)" : "#fff",
                          color: isActive ? "#fff" : COLORS.primary,
                          border: `1px solid ${
                            isActive ? "rgba(255,255,255,.35)" : COLORS.border
                          }`,
                          display: "grid",
                          placeItems: "center",
                          boxShadow: isActive ? "inset 0 0 0 1px rgba(255,255,255,.1)" : "none",
                        }}
                      >
                        {m.icon}
                      </div>
                      <div style={{ fontWeight: 800, letterSpacing: 0.2 }}>{m.label}</div>
                      <div style={menuCountWrap}>
                        <span key={`${m.key}-${count}`} style={menuCountBadge(isActive)}>{count}</span>
                        <FiChevronRight style={{ opacity: isActive ? 1 : 0.25 }} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </aside>

            {/* Right content */}
            <section
              style={{
                background: COLORS.card,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 14,
                padding: 16,
                minHeight: 420,
                boxShadow: "0 1px 2px rgba(0,0,0,.05)",
              }}
            >
              {/* Section header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <div style={{ fontWeight: 900, color: COLORS.text, fontSize: 18 }}>
                  {labelFor(activeMenu)} Events
                </div>
                <div style={{ color: COLORS.subtext, fontSize: 13 }}>
                  {`Showing ${filteredEvents.length} ${labelFor(activeMenu).toLowerCase()} event${
                    filteredEvents.length === 1 ? "" : "s"
                  }`}
                </div>
              </div>

              <hr
                style={{
                  border: 0,
                  borderTop: `1px solid ${COLORS.border}`,
                  margin: "10px 0 14px",
                }}
              />

              {/* Content by menu */}
              <EventList
                menuKey={activeMenu}
                loading={loadingEvents}
                error={eventError}
                items={filteredEvents}
                onOpen={(id) => nav(`/events/create_event_form2?eid=${encodeURIComponent(id)}`)}
                onDelete={activeMenu === "DRAFT" ? onDeleteDraft : null}
                deletingIds={deletingIds}
              />
            </section>
          </div>
        )}

        {/* Offer tab */}
        {activeTab === "OFFER" && (
          <div style={{ maxWidth: 980, margin: "0 auto" }}>
            <div
              style={{
                background: COLORS.card,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 14,
                padding: 16,
                boxShadow: "0 1px 2px rgba(0,0,0,.05)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <div
                  style={{
                    width: 34,
                    height: 34,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: 8,
                    background: "#fff",
                    border: `1px solid ${COLORS.border}`,
                    color: COLORS.primary,
                  }}
                >
                  <FiTag />
                </div>
                <div style={{ fontWeight: 900, fontSize: 18, color: COLORS.text }}>
                  Create Event / Venue Offer
                </div>
              </div>

              <p style={{ marginTop: 0, color: COLORS.subtext }}>
                Configure a discount or promo that applies to specific events or venues. Saving will
                create a document in Firestore collection <b>event_venue_offers</b>.
              </p>

              <form onSubmit={onSaveOffer} style={{ display: "grid", gap: 12 }}>
                <Field
                  label="Offer Title"
                  value={offerTitle}
                  onChange={setOfferTitle}
                  placeholder="e.g. Early Bird 15% Off"
                />

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <Select
                    label="Offer Scope"
                    value={scope}
                    onChange={setScope}
                    options={[
                      { value: "event", label: "Event Offer" },
                      { value: "venue", label: "Venue Booking Offer" },
                    ]}
                  />
                  <Select
                    label="Offer Type"
                    value={offerType}
                    onChange={setOfferType}
                    options={[
                      { value: "percent", label: "Percentage Off (%)" },
                      { value: "flat", label: "Flat Amount (₹)" },
                      { value: "bogo", label: "BOGO (Buy One Get One)" },
                    ]}
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                  <Field
                    label={
                      offerType === "percent"
                        ? "Discount (%)"
                        : offerType === "flat"
                        ? "Discount Amount (₹)"
                        : "BOGO Enabled"
                    }
                    value={offerType === "bogo" ? "Yes" : discountValue}
                    onChange={(v) => offerType !== "bogo" && setDiscountValue(v)}
                    placeholder={offerType === "percent" ? "e.g. 15" : offerType === "flat" ? "e.g. 200" : ""}
                    type={offerType === "bogo" ? "text" : "number"}
                    readOnly={offerType === "bogo"}
                  />
                  <Field
                    label="Max Discount (₹)"
                    value={maxDiscount}
                    onChange={setMaxDiscount}
                    placeholder="Optional cap"
                    type="number"
                  />
                  <Field
                    label="Min Order Amount (₹)"
                    value={minOrderAmount}
                    onChange={setMinOrderAmount}
                    placeholder="Optional threshold"
                    type="number"
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                  <Field
                    label="Promo Code"
                    value={code}
                    onChange={(v) => setCode(v.toUpperCase())}
                    placeholder="e.g. EARLYBIRD15"
                  />
                  <Field
                    label="Usage Limit"
                    value={usageLimit}
                    onChange={setUsageLimit}
                    placeholder="Total redemptions (optional)"
                    type="number"
                  />
                  <Select
                    label="Status"
                    value={status}
                    onChange={setStatus}
                    options={[
                      { value: "active", label: "Active" },
                      { value: "inactive", label: "Inactive" },
                    ]}
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <Field
                    label="Valid From"
                    value={validFrom}
                    onChange={setValidFrom}
                    placeholder="YYYY-MM-DD"
                    type="date"
                  />
                  <Field
                    label="Valid To"
                    value={validTo}
                    onChange={setValidTo}
                    placeholder="YYYY-MM-DD"
                    type="date"
                  />
                </div>

                <Field
                  label={scope === "event" ? "Applicable Event ID(s)" : "Applicable Venue ID(s)"}
                  value={appliesTo}
                  onChange={setAppliesTo}
                  placeholder="Comma-separated IDs (e.g. EV123, EV456)"
                />

                <label style={{ display: "block" }}>
                  <span style={labelStyle}>Terms & Conditions</span>
                  <textarea
                    value={terms}
                    onChange={(e) => setTerms(e.target.value)}
                    placeholder="Enter any restrictions, exclusions, or notes"
                    rows={4}
                    style={{ ...inputStyle, height: 120, resize: "vertical" }}
                  />
                </label>

                {saveError && <div style={{ color: "#b91c1c", fontSize: 13, marginTop: 4 }}>{saveError}</div>}
                {saveMsg && <div style={{ color: COLORS.primary, fontSize: 13, marginTop: 4 }}>{saveMsg}</div>}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                  <button
                    type="button"
                    onClick={() => {
                      setOfferTitle("");
                      setScope("event");
                      setOfferType("percent");
                      setDiscountValue("");
                      setMaxDiscount("");
                      setMinOrderAmount("");
                      setCode("");
                      setUsageLimit("");
                      setValidFrom("");
                      setValidTo("");
                      setAppliesTo("");
                      setTerms("");
                      setStatus("active");
                      setSaveMsg("");
                      setSaveError("");
                    }}
                    style={ghostBtn}
                  >
                    Reset
                  </button>
                  <button type="submit" disabled={saving} style={primaryBtn}>
                    {saving ? "Saving…" : "Create Offer"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Scan tab */}
        {activeTab === "SCAN" && (
          <div style={{ maxWidth: 980, margin: "0 auto" }}>
            <div
              style={{
                background: COLORS.card,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 14,
                padding: 24,
                minHeight: 320,
                display: "grid",
                placeItems: "center",
                boxShadow: "0 1px 2px rgba(0,0,0,.05)",
                textAlign: "center",
                color: COLORS.subtext,
              }}
            >
              <div
                style={{
                  width: 84,
                  height: 84,
                  borderRadius: 18,
                  border: `1px solid ${COLORS.border}`,
                  background: "#fff",
                  color: COLORS.primary,
                  display: "grid",
                  placeItems: "center",
                  marginBottom: 12,
                  boxShadow: "0 8px 22px rgba(0,0,0,.06)",
                }}
              >
                <FaQrcode size={40} />
              </div>
              <div style={{ fontWeight: 900, color: COLORS.text, fontSize: 18 }}>Scanning Guest List</div>
              <div style={{ marginTop: 6 }}>Under development — this page will host the QR scanner and validation UI.</div>
            </div>
          </div>
        )}
      </main>

      {/* Profile Drawer */}
      {profileOpen && (
        <div style={drawerBackdrop} onClick={() => setProfileOpen(false)}>
          <div style={{ ...drawerPanel, transform: "translateX(0)" }} onClick={(e) => e.stopPropagation()}>
            <div style={drawerHeader}>
              <div style={{ fontWeight: 900 }}>Profile</div>
              <button
                type="button"
                onClick={() => setProfileOpen(false)}
                style={drawerCloseBtn}
                aria-label="Close profile panel"
              >
                <FiX />
              </button>
            </div>

            <div style={drawerBody}>
              <OrganiserProfile organiserUID={organiserUID} contactName={contactName} />
            </div>
          </div>

          {/* off-screen panel to preserve slide-in animation */}
          <div
            style={{
              ...drawerPanel,
              transform: "translateX(100%)",
              pointerEvents: "none",
              visibility: "hidden",
            }}
          />
        </div>
      )}

      {/* New Event FAB */}
      <button
        type="button"
        aria-label="Create a new event"
        title="New Event"
        style={fabBtn}
        onClick={onNewEventClick}
      >
        <FiPlus /> New Event
      </button>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
   DRAFT LIST + CARD (PRO)
   ────────────────────────────────────────────────────────── */
function EventList({ menuKey, loading, error, items, onOpen, onDelete, deletingIds = {} }) {
  const label = labelFor(menuKey).toLowerCase();
  if (loading) return <div style={{ padding: 20, color: COLORS.subtext }}>Loading {label} events…</div>;
  if (error) return <div style={{ padding: 20, color: "#b91c1c" }}>{error}</div>;
  if (!items || items.length === 0)
    return <div style={{ padding: 20, color: COLORS.subtext }}>No {label} events found.</div>;

  return (
    <div
      style={{
        display: "grid",
        gap: 14,
        gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
      }}
    >
      {items.map((ev) => (
        <EventCard
          key={ev.id}
          ev={ev}
          onOpen={() => onOpen(ev.id)}
          onDelete={onDelete ? () => onDelete(ev.id) : null}
          deleting={!!deletingIds[ev.id]}
        />
      ))}
    </div>
  );
}

function EventCard({ ev, onOpen, onDelete, deleting }) {
  const name = ev.name || "Untitled event";
  // Try multiple locations for venue name
  const venue =
    ev?.schedule?.venueName ||
    ev?.venueDetails?.venueName ||
    ev?.schedule?.venueDetails?.venueName ||
    "—";
  const shows = Array.isArray(ev?.schedule?.shows) ? ev.schedule.shows : [];
  const firstDT = getFirstShowDate(shows); // Date or null
  const whenStr = firstDT
    ? `${firstDT.toLocaleDateString(undefined, {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
      })} • ${firstDT.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`
    : "—";

  const cat = ev?.category || "";
  const subcat = ev?.subCategory || "";

  const desc =
    typeof ev?.about?.description === "string" && ev.about.description.trim()
      ? trimTo(ev.about.description.trim(), 110)
      : "";

  // Prefer explicit URLs; if missing, resolve Storage path
  const initialHero = pickHeroImage(ev);
  const [hero, setHero] = useState(initialHero);

  useEffect(() => {
    let mounted = true;
    async function resolveFromPath() {
      try {
        const storage = getStorage();
        const hbPath = ev?.images?.horizontalBanner?.path || null;
        const vpPath = ev?.images?.verticalPoster?.path || null;
        const tryPaths = [hbPath, vpPath].filter(Boolean);
        for (const p of tryPaths) {
          try {
            const url = await getDownloadURL(sRef(storage, p));
            if (mounted && url) {
              setHero(url);
              return;
            }
          } catch {
            // try next
          }
        }
      } catch {
        // ignore
      }
    }
    const picked = pickHeroImage(ev);
    setHero(picked);
    if (!picked) resolveFromPath();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ev]);

  const artists = Array.isArray(ev?.artists?.selected) ? ev.artists.selected : [];

  const tk = summarizeTickets(ev?.tickets);
  const ticketsLine =
    tk.count > 0
      ? tk.min === tk.max
        ? `₹${tk.min.toLocaleString()} • ${tk.totalQty} total`
        : `₹${tk.min.toLocaleString()}–₹${tk.max.toLocaleString()} • ${tk.totalQty} total`
      : "—";

  const expired = isEventExpired(ev);
  const status = expired && String(ev?.status || "").toLowerCase() === "live" ? "PAST" : (ev?.status || "draft").toUpperCase();

  return (
    <div
      role="button"
      onClick={onOpen}
      title="Open event"
      style={proCardWrapper}
    >
      {/* Media */}
      <div style={mediaWrap}>
        {hero ? (
          <img src={hero} alt={`${name} banner`} style={mediaImg} />
        ) : (
          <div style={mediaFallback}>
            <div style={mediaFallbackBadge}>No Image</div>
          </div>
        )}

        {/* Top-left status chip */}
        <div style={statusChip}>
          {status === "DRAFT" ? "WIP / DRAFT" : status}
        </div>

        {/* Top-right quick action: DELETE */}
        {onDelete && (
        <button
          type="button"
          style={{
            ...trashBtn,
            cursor: deleting ? "wait" : "pointer",
            opacity: deleting ? 0.6 : 1,
          }}
          title={deleting ? "Deleting…" : "Delete draft"}
          onClick={(e) => {
            e.stopPropagation();
            if (!deleting) onDelete();
          }}
          disabled={deleting}
        >
          <FiTrash2 />
        </button>
        )}
      </div>

      {/* Body */}
      <div style={cardBody}>
        {/* Title + badges */}
        <div style={{ display: "grid", gap: 8 }}>
          <div style={titleText}>{name}</div>

          <div style={badgeRow}>
            {cat && <span style={{ ...badge, background: "#EEF2FF", borderColor: "#C7D2FE", color: "#3730A3" }}>{cat}</span>}
            {subcat && (
              <span style={{ ...badge, background: "#ECFEFF", borderColor: "#A5F3FC", color: "#075985" }}>
                {subcat}
              </span>
            )}
            <span style={{ ...badge, background: "#FEF3C7", borderColor: "#FDE68A", color: "#78350F" }}>
              ID: {ev.id.slice(0, 6)}…
            </span>
          </div>
        </div>

        {/* Meta rows */}
        <div style={metaRow}>
          <span style={metaIcon}><FiCalendar /></span>
          <span style={metaText}>{whenStr}</span>
        </div>
        <div style={metaRow}>
          <span style={metaIcon}><FiMapPin /></span>
          <span style={metaText}>{venue}</span>
        </div>
        <div style={metaRow}>
          <span style={metaIcon}><FiTag /></span>
          <span style={metaText}>{tk.count > 0 ? `${tk.count} ticket type${tk.count > 1 ? "s" : ""} • ${ticketsLine}` : "No tickets added yet"}</span>
        </div>

        {/* Description */}
        {desc && <div style={descText}>{desc}</div>}

        {/* Artists */}
        {artists.length > 0 && (
          <div style={{ display: "grid", gap: 8 }}>
            <div style={artistsHdr}>
              <FiUsers /> Artists
            </div>
            <div style={artistsRow}>
              {artists.slice(0, 5).map((a, i) => (
                <ArtistAvatar key={`${a.name}-${i}`} a={a} />
              ))}
              {artists.length > 5 && (
                <div style={moreAvatar}>+{artists.length - 5}</div>
              )}
            </div>
          </div>
        )}

        {/* CTA */}
        <div style={ctaRow}>
          <button type="button" style={ctaBtnPrimary}>
            Continue editing
          </button>
        </div>
      </div>
    </div>
  );
}

function ArtistAvatar({ a }) {
  const url = a?.photoURL;
  const name = a?.name || "Artist";
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();

  return (
    <div title={name} style={artistAvatarWrap}>
      {url ? (
        <img src={url} alt={name} style={artistImg} />
      ) : (
        <span style={artistInitials}>{initials}</span>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
   HELPERS
   ────────────────────────────────────────────────────────── */
function summarizeTickets(tickets) {
  const arr = Array.isArray(tickets) ? tickets : [];
  if (arr.length === 0) return { count: 0, min: 0, max: 0, totalQty: 0 };
  const prices = arr.map((t) => Number(t.price || 0));
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const totalQty = arr.reduce((s, t) => s + (Number(t.qty || 0) || 0), 0);
  return { count: arr.length, min, max, totalQty };
}

function pickHeroImage(ev) {
  // NEW: prefer structured URLs if available
  const hbUrl = ev?.images?.horizontalBanner?.url;
  const vpUrl = ev?.images?.verticalPoster?.url;
  if (isHttpUrl(hbUrl)) return hbUrl;
  if (isHttpUrl(vpUrl)) return vpUrl;

  // OLD: legacy fields some projects used
  const headerUrl = ev?.images?.headerImageUrl || ev?.images?.headerURL || ev?.images?.bannerURL;
  const posterUrl = ev?.images?.verticalPosterUrl || ev?.images?.verticalPosterURL;
  if (isHttpUrl(headerUrl)) return headerUrl;
  if (isHttpUrl(posterUrl)) return posterUrl;

  // Fallback: first artist photo
  const firstArtist = Array.isArray(ev?.artists?.selected) ? ev.artists.selected[0] : null;
  if (isHttpUrl(firstArtist?.photoURL)) return firstArtist.photoURL;

  // If only Storage paths exist, EventCard will resolve them asynchronously
  return null;
}

function isHttpUrl(s) {
  return typeof s === "string" && /^https?:\/\//i.test(s);
}

function trimTo(s, n) {
  if (s.length <= n) return s;
  const soft = s.slice(0, n);
  const cut = soft.lastIndexOf(" ");
  return (cut > 40 ? soft.slice(0, cut) : soft) + "…";
}

function getFirstShowDate(shows) {
  if (!Array.isArray(shows)) return null;
  let best = null;
  for (const s of shows) {
    if (!s?.date) continue;
    if (Array.isArray(s.times) && s.times.length > 0) {
      for (const t of s.times) {
        const d = parseDateTime(s.date, t);
        if (d && (!best || d < best)) best = d;
      }
    } else {
      const d = parseDateTime(s.date, "00:00");
      if (d && (!best || d < best)) best = d;
    }
  }
  return best;
}

function eventBelongsInMenu(ev, menuKey) {
  const status = String(ev?.status || "").trim().toLowerCase();
  const expired = isEventExpired(ev);
  if (menuKey === "LIVE") return status === "live" && !expired;
  if (menuKey === "PAST") return ["past", "completed", "ended"].includes(status) || (status === "live" && expired);
  return true;
}

function isEventExpired(ev) {
  const end = getLastShowEndDate(ev);
  return !!end && end.getTime() < Date.now();
}

function getLastShowEndDate(ev) {
  const shows = Array.isArray(ev?.schedule?.shows) ? ev.schedule.shows : [];
  const durationMin = Number(ev?.additionalInfo?.durationMin || ev?.durationMin || 0);
  let last = null;
  for (const s of shows) {
    if (!s?.date) continue;
    const times = Array.isArray(s.times) && s.times.length ? s.times : ["23:59"];
    for (const t of times) {
      const start = parseDateTime(s.date, t || "23:59");
      if (!start) continue;
      const end = new Date(start.getTime() + Math.max(durationMin, 0) * 60 * 1000);
      if (!last || end > last) last = end;
    }
  }
  return last;
}

function parseDateTime(dateISO, hhmm) {
  const t = (hhmm || "00:00").slice(0, 5);
  const d = new Date(`${dateISO}T${t}:00`);
  return isNaN(d.getTime()) ? null : d;
}

/* ──────────────────────────────────────────────────────────
   SMALL HELPERS + UI BITS
   ────────────────────────────────────────────────────────── */
function labelFor(k) {
  switch (k) {
    case "LIVE":
      return "Live";
    case "REVIEW":
      return "Review";
    case "DRAFT":
      return "Draft";
    case "PAST":
      return "Past";
    case "UNLISTED":
      return "Unlisted & Closed";
    default:
      return k;
  }
}

function menuFromTabParam(tab) {
  const value = String(tab || "").trim().toLowerCase();
  if (value === "live") return "LIVE";
  if (value === "review" || value === "pending" || value === "submitted") return "REVIEW";
  if (value === "past") return "PAST";
  if (value === "unlisted" || value === "closed" || value === "close") return "UNLISTED";
  return "DRAFT";
}

function getBigIcon(k) {
  const size = 36;
  switch (k) {
    case "LIVE":
      return <FiPlay size={size} />;
    case "REVIEW":
      return <FiClock size={size} />;
    case "DRAFT":
      return <FiEdit3 size={size} />;
    case "PAST":
      return <FiClock size={size} />;
    case "UNLISTED":
      return <FiEyeOff size={size} />;
    default:
      return <FiPlay size={size} />;
  }
}

function splitIds(s) {
  return s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function buildMenuCounts(rows = []) {
  const counts = Object.fromEntries(MENU.map((m) => [m.key, 0]));
  rows.forEach((ev) => {
    MENU.forEach((m) => {
      if (statusMatchesMenu(ev, m.key) && eventBelongsInMenu(ev, m.key)) counts[m.key] += 1;
    });
  });
  return counts;
}

function statusMatchesMenu(ev, menuKey) {
  const statuses = STATUS_FILTERS[menuKey] || [];
  return statuses.includes(String(ev?.status || "").trim().toLowerCase());
}

function Field({ label, value, onChange, placeholder, type = "text", readOnly = false }) {
  return (
    <label style={{ display: "block" }}>
      <span style={labelStyle}>{label}</span>
      <input
        type={type}
        value={value}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={inputStyle}
      />
    </label>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label style={{ display: "block" }}>
      <span style={labelStyle}>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

// eslint-disable-next-line no-unused-vars
function EmptyState({ activeMenu }) {
  return (
    <div
      style={{
        display: "grid",
        placeItems: "center",
        padding: "40px 12px",
        color: COLORS.subtext,
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: 74,
          height: 74,
          borderRadius: 16,
          border: `1px solid ${COLORS.border}`,
          display: "grid",
          placeItems: "center",
          boxShadow: "0 8px 22px rgba(0,0,0,.06)",
          marginBottom: 12,
          background: "#fff",
          color: COLORS.primary,
        }}
      >
        {getBigIcon(activeMenu)}
      </div>
      <div style={{ fontWeight: 800 }}>No data loaded yet</div>
      <div style={{ marginTop: 6 }}>Start typing in the search box or pick a status on the left.</div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────
   SHARED STYLES
   ────────────────────────────────────────────────────────── */
const inputStyle = {
  width: "100%",
  height: 44,
  padding: "0 12px",
  marginTop: 6,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  outline: "none",
  background: "#fff",
  color: COLORS.text,
};

const labelStyle = {
  fontSize: 13,
  color: COLORS.text,
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const primaryBtn = {
  height: 44,
  padding: "0 16px",
  borderRadius: 10,
  border: "none",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
};

const ghostBtn = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  height: 44,
  padding: "0 16px",
  borderRadius: 10,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
};

const drawerBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.45)",
  zIndex: 80,
  display: "block",
};

const drawerPanelBase = {
  position: "fixed",
  top: 0,
  right: 0,
  height: "100vh",
  width: "50vw",
  maxWidth: 720,
  background: "#fff",
  borderLeft: `1px solid ${COLORS.border}`,
  boxShadow: "0 10px 30px rgba(0,0,0,.25)",
  display: "flex",
  flexDirection: "column",
  transition: "transform .22s ease-out",
};

const drawerPanel = {
  ...drawerPanelBase,
};

const drawerHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: 12,
  borderBottom: `1px solid ${COLORS.border}`,
};

const drawerCloseBtn = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  height: 36,
  width: 36,
  borderRadius: 8,
  cursor: "pointer",
  display: "grid",
  placeItems: "center",
};

const drawerBody = {
  padding: 12,
  overflowY: "auto",
  flex: 1,
};

const fabBtn = {
  position: "fixed",
  right: 24,
  bottom: 24,
  zIndex: 90,
  height: 48,
  border: "none",
  borderRadius: 999,
  padding: "0 18px",
  fontWeight: 800,
  color: "#fff",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  boxShadow: "0 14px 28px rgba(242,12,83,.25)",
};

/* ──────────────────────────────────────────────────────────
   CARD-SPECIFIC STYLES
   ────────────────────────────────────────────────────────── */
const menuCountWrap = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 8,
};

const menuCountBadge = (active) => ({
  minWidth: 28,
  height: 28,
  padding: "0 8px",
  borderRadius: 999,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 13,
  fontWeight: 900,
  background: active ? "rgba(255,255,255,.22)" : "#fff1f2",
  color: active ? "#fff" : COLORS.primary,
  border: `1px solid ${active ? "rgba(255,255,255,.35)" : "#fecdd3"}`,
  boxShadow: active ? "inset 0 0 0 1px rgba(255,255,255,.08)" : "0 8px 16px rgba(242,12,83,.08)",
  animation: "countPop .28s ease-out",
});

const proCardWrapper = {
  display: "grid",
  gridTemplateRows: "160px 1fr",
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 16,
  overflow: "hidden",
  cursor: "pointer",
  boxShadow: "0 2px 10px rgba(0,0,0,.05)",
  transition: "transform .12s ease, box-shadow .18s ease, border-color .18s ease",
};
const mediaWrap = { position: "relative", width: "100%", height: 160, background: "#f8fafc" };
const mediaImg = { width: "100%", height: "100%", objectFit: "cover" };
const mediaFallback = {
  position: "absolute",
  inset: 0,
  display: "grid",
  placeItems: "center",
  background:
    "linear-gradient(135deg, rgba(242,12,83,.08), rgba(251,108,51,.08))",
  borderBottom: `1px solid ${COLORS.border}`,
};
const mediaFallbackBadge = {
  fontSize: 12,
  fontWeight: 800,
  color: COLORS.subtext,
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  padding: "4px 8px",
  borderRadius: 999,
};
const statusChip = {
  position: "absolute",
  top: 10,
  left: 10,
  background: "#FEF3C7",
  color: "#78350F",
  border: "1px solid #FDE68A",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 800,
  padding: "4px 8px",
  boxShadow: "0 1px 2px rgba(0,0,0,.06)",
};
const trashBtn = {
  position: "absolute",
  top: 10,
  right: 10,
  width: 34,
  height: 34,
  borderRadius: 10,
  display: "grid",
  placeItems: "center",
  background: "#fff",
  color: "#991b1b",
  border: "1px solid #fecaca",
  boxShadow: "0 2px 8px rgba(0,0,0,.08)",
};
const cardBody = {
  display: "grid",
  gap: 10,
  padding: 14,
};
const titleText = {
  fontWeight: 900,
  fontSize: 18,
  color: COLORS.text,
  lineHeight: 1.25,
  wordBreak: "break-word",
};
const badgeRow = { display: "flex", flexWrap: "wrap", gap: 6 };
const badge = {
  fontSize: 11,
  fontWeight: 800,
  padding: "3px 8px",
  borderRadius: 999,
  border: "1px solid",
};
const metaRow = {
  display: "grid",
  gridTemplateColumns: "20px 1fr",
  alignItems: "center",
  gap: 8,
};
const metaIcon = { display: "grid", placeItems: "center", color: COLORS.subtext };
const metaText = {
  fontSize: 13,
  color: COLORS.text,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const descText = {
  fontSize: 13,
  color: COLORS.subtext,
  lineHeight: 1.45,
  marginTop: 2,
};
const artistsHdr = {
  display: "inline-grid",
  gridAutoFlow: "column",
  alignItems: "center",
  gap: 6,
  fontWeight: 800,
  fontSize: 12,
  color: COLORS.text,
};
const artistsRow = { display: "flex", alignItems: "center", gap: 8 };
const artistAvatarWrap = {
  width: 28,
  height: 28,
  borderRadius: "50%",
  overflow: "hidden",
  display: "grid",
  placeItems: "center",
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
};
const artistImg = { width: "100%", height: "100%", objectFit: "cover" };
const artistInitials = { fontSize: 11, fontWeight: 900, color: COLORS.subtext };
const moreAvatar = {
  minWidth: 28,
  height: 28,
  borderRadius: "50%",
  display: "grid",
  placeItems: "center",
  border: `1px solid ${COLORS.border}`,
  background: "#f3f4f6",
  fontSize: 11,
  fontWeight: 900,
  color: COLORS.text,
  padding: "0 6px",
};
const ctaRow = { display: "flex", justifyContent: "flex-end", marginTop: 4 };
const ctaBtnPrimary = {
  height: 34,
  padding: "0 12px",
  borderRadius: 10,
  border: "none",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
};

/* subtle hover for the whole card */
Object.assign(proCardWrapper, {
  onMouseEnter: null,
});
